import { useCallback } from 'react';
import scrollIntoView from 'scroll-into-view-if-needed';

import useEvent from '@lyearn/core/hooks/useEvent';
import Pipeline, { Next } from '@lyearn/core/utils/pipeline';
import { useTranslation } from '@lyearn/i18n';
import { useSnackbar } from '@lyearn/ui';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import useNavigation from '@/hooks/useNavigation';
import { useUserContext } from '@/modules/auth/hooks/useUser';
import useIsPublishable from '@/modules/content/hooks/useIsPublishable';
import { useEmbeddedQuizEditor } from '@/modules/corporate/modules/course/components/QuizCardContainer/components/QuizEditorModal/context';
import { REQUIRED_TAG_ERROR_CODE } from '@/modules/corporate/modules/tags/utils';
import routes from '@/pages/corporate/routes';
import { ResourceStatusEnum, UpdateQuestionEntity, UpdateQuizInput } from '@/types/schema';
import { getErrorCode } from '@/utils/api/response';

import { QuizEditorSidebarTabs } from '../../../components/QuizEditorSidebar/types';
import {
  isQuestionGenerationSettingsValid,
  isSectionRandomizationSettingsValid,
} from '../../../components/QuizEditorSidebar/views/QuizSettings/components/Dialogs/QuizRandomization/utils';
import { QuizEditorDetailsFragment } from '../../../graphql/fragments/generated/QuizEditorContent';
import { adaptQuiz } from '../../../utils/adapters';
import { removeEmptyElements } from '../../../utils/questions';
import { validateQuestions } from '../../../utils/validations';
import useQuizEditorMutations from '../../useQuizEditorMutations';
import useQuizEditorState from '../../useQuizEditorState';

interface Params {
  quiz: QuizEditorDetailsFragment;
  state: ReturnType<typeof useQuizEditorState>;
  isQuizPublished: Boolean;
  onUpdateQuestions: ReturnType<typeof useQuizEditorMutations>['onUpdateQuestions'];
  onUpdateQuiz: ReturnType<typeof useQuizEditorMutations>['onUpdateQuiz'];
}

const usePublishQuiz = ({
  quiz,
  isQuizPublished,
  state,
  onUpdateQuestions,
  onUpdateQuiz,
}: Params) => {
  const { t } = useTranslation();
  const { showErrorSnackbar, showSnackbar } = useSnackbar();
  const navigation = useNavigation();
  const user = useUserContext();
  const { [FeatureFlags.EnableSubWorkspacesForQuestions]: isQuestionSubWorkspacesEnabled } =
    useFeatureFlags();

  const embeddedEditor = useEmbeddedQuizEditor();
  const { fetchIsPublishable } = useIsPublishable(quiz.id);

  const {
    questions,
    publishActionRef,

    setUiState,
  } = state;

  const handleTagError = useCallback(async () => {
    if (embeddedEditor) {
      return;
    }
    await navigation.replace(routes.QuizEditor(quiz.id), {
      query: {
        validationError: 'tag',
        tab: QuizEditorSidebarTabs.Settings,
      },
      appendQuery: false,
    });
  }, [embeddedEditor, navigation, quiz.id]);

  const onUpdateQuizData = useCallback(
    async (quizData: Partial<UpdateQuizInput>) => {
      const currentSubWorkspace = user?.currentSubWorkspace;
      const { response } = await onUpdateQuiz({
        ...adaptQuiz(quiz, currentSubWorkspace),
        ...quizData,
      });
      return response;
    },
    [onUpdateQuiz, quiz, user?.currentSubWorkspace],
  );

  const navigateToQuestionTab = useCallback(() => {
    if (embeddedEditor) {
      return;
    }
    navigation.replace(routes.QuizEditor(quiz.id), {
      query: { tab: QuizEditorSidebarTabs.Question },
      appendQuery: false,
    });
  }, [embeddedEditor, navigation, quiz.id]);

  const validateQuestionsAssignedToSections = useCallback(
    (updatedQuestions: UpdateQuestionEntity[]) => {
      if (!quiz.quizSections?.length) {
        return true;
      }

      const hasUnsectionedQuestion = (
        updatedQuestions as Array<UpdateQuestionEntity & { sectionId?: string | null }>
      ).some((question) => (question.sectionId ?? null) === null);

      if (!hasUnsectionedQuestion) {
        return true;
      }

      setUiState({ isPublishing: false });
      showErrorSnackbar({ message: t('QuizCreator:Validations.AddQuestionToSection') });
      navigateToQuestionTab();

      return false;
    },
    [navigateToQuestionTab, quiz.quizSections, setUiState, showErrorSnackbar, t],
  );

  async function quizUiValidation() {
    const updatedQuestions = isQuizPublished ? questions : removeEmptyElements(questions);

    if (!isQuizPublished) {
      const questionsToUpdate = updatedQuestions.filter(
        (updatedQuestion, index) => updatedQuestion !== questions[index],
      );
      questionsToUpdate.length && (await onUpdateQuestions(questionsToUpdate));
    }
    const questionErrors = validateQuestions(t, updatedQuestions, isQuestionSubWorkspacesEnabled);

    if (Object.keys(questionErrors).length) {
      setUiState({ errors: questionErrors, isPublishing: false });
      navigateToQuestionTab();
      setTimeout(() => {
        const listItem = document.querySelector('[data-question-error]');
        listItem && scrollIntoView(listItem, { behavior: 'smooth', block: 'start' });
      }, 0);
      return;
    }

    if (!isQuestionGenerationSettingsValid({ quiz, questions: updatedQuestions })) {
      setUiState({ isPublishing: false });
      if (!embeddedEditor) {
        navigation.replace(routes.QuizEditor(quiz.id), {
          query: { validationError: 'randomization', tab: QuizEditorSidebarTabs.Settings },
          appendQuery: false,
        });
      }
      return;
    }

    if (quiz.quizSections?.length) {
      const questionsWithSection = updatedQuestions as Array<
        UpdateQuestionEntity & { sectionId?: string | null }
      >;

      for (const section of quiz.quizSections) {
        const sectionQuestionsCount = questionsWithSection.filter(
          (q) => (q.sectionId ?? null) === section._id,
        ).length;
        if (sectionQuestionsCount === 0) {
          setUiState({ isPublishing: false });
          showErrorSnackbar({
            message: t('QuizCreator:Validations.SectionHasNoQuestions', {
              sectionName: section.name ?? t('QuizCreator:Sections.SectionName'),
            }),
          });
          navigateToQuestionTab();
          return;
        }
      }

      const sectionValidation = isSectionRandomizationSettingsValid({
        sections: quiz.quizSections,
        questions: questionsWithSection,
      });
      if (!sectionValidation.valid) {
        setUiState({ isPublishing: false });
        showErrorSnackbar({
          message: t('QuizCreator:Validations.SectionRandomizationInvalid'),
        });
        navigateToQuestionTab();
        return;
      }
    }

    return true;
  }

  const onPublishQuiz = useEvent(async () => {
    if (!publishActionRef.current) {
      return;
    }

    const action = publishActionRef.current;

    type Context = {
      updateQuizData?: Awaited<ReturnType<typeof onUpdateQuizData>>;
      updatedQuestions?: UpdateQuestionEntity[];
    };

    const pipeline = Pipeline<Context>();

    async function uiValidation(ctx: Context, next: Next) {
      const updatedQuestions = isQuizPublished ? questions : removeEmptyElements(questions);
      ctx.updatedQuestions = updatedQuestions;

      const response = await quizUiValidation();
      if (response && validateQuestionsAssignedToSections(updatedQuestions)) {
        next();
      }
    }

    async function updateQuestions(ctx: Context, next: Next) {
      if (ctx.updatedQuestions) {
        const questionsToUpdate = ctx.updatedQuestions.filter(
          (updatedQuestion, index) => updatedQuestion !== questions[index],
        );
        questionsToUpdate.length && (await onUpdateQuestions(questionsToUpdate));
      }
      setUiState({ isPublishing: true });
      next();
    }

    async function serverValidation(_ctx: Context, next: Next) {
      const result = await fetchIsPublishable();
      const publishable = result?.publishable;
      // If content is publishable, move ahead
      if (publishable?.isPublishable) {
        next();
      }

      // Show error if content is not publishable due to required tag error
      if (!publishable?.isPublishable && publishable?.reason === REQUIRED_TAG_ERROR_CODE) {
        await handleTagError();
        setUiState({ isPublishing: false });
      }
    }

    async function updateQuiz(ctx: Context, next: Next) {
      ctx.updateQuizData = await onUpdateQuizData({
        status: ResourceStatusEnum.Published,
        publishConfig: action.payload.publishConfig,
        settings: quiz.settings,
      });
      next();
    }

    async function errorHandler(ctx: Context, next: Next) {
      const { updateQuizData } = ctx;

      // If no error, move ahead
      if (updateQuizData && !updateQuizData.error) {
        next();
      }

      const errorCode = getErrorCode(updateQuizData?.error);
      if (errorCode === REQUIRED_TAG_ERROR_CODE) {
        await handleTagError();
      }

      setUiState({ isPublishing: false });
    }

    async function successHandler(_ctx: Context, next: Next) {
      showSnackbar({ message: action.payload.snackbarProps.message });
      setUiState({ isPublishing: false });
      if (embeddedEditor) {
        embeddedEditor.onClose();
      } else {
        await navigation.replace(routes.AdminQuizDetails(quiz.id));
      }
      next();
    }

    if (!questions.length) {
      showErrorSnackbar({ message: t('QuizCreator:Validations.QuizCannotBeEmpty') });
      return;
    }

    pipeline.push(
      uiValidation,
      updateQuestions,
      serverValidation,
      updateQuiz,
      errorHandler,
      successHandler,
    );

    await pipeline.execute({});
  });

  return { onPublishQuiz, onUpdateQuizData, quizUiValidation };
};

export default usePublishQuiz;
